/** Main navigation, kept free of React so its rules are unit-tested. Pages a role cannot open are not listed. */
export type NavigationIcon = 'home' | 'collections' | 'questions' | 'needs-you' | 'today' | 'customers' | 'approvals' | 'cash' | 'inbox' | 'calls' | 'reports' | 'mail' | 'slack' | 'policy' | 'integrations' | 'credit' | 'settings';
export type NavigationGroup = 'work' | 'records' | 'admin';
export const navigationGroupLabels: Record<NavigationGroup, string> = { work: 'Work', records: 'Records', admin: 'Admin' };
export interface NavigationItem { id: string; label: string; icon: NavigationIcon; group: NavigationGroup }

/**
 * Old routes that now live on another page. Slack setup and Connections merged into Integrations; Needs you became
 * Collections (2026-10-31), and its deep links (`#needs-you/<item key>`) open that item's case there.
 */
const redirects: Record<string, string> = { 'slack-settings': 'integrations', connections: 'integrations', 'needs-you': 'collections' };
const deepRedirects = new Set(['needs-you']);

/** Top-level hashes the shell knows how to render. Unknown hashes show Not found. */
const knownBases = new Set([
  'home', 'collections', 'questions', 'today', 'approvals', 'inbox', 'customers', 'calls', 'reports',
  'integrations', 'policy', 'demo-mailbox', 'demo-slack', 'cash', 'credit', 'settings', 'observability',
]);

export function resolvePage(hash: string): string {
  // A sign-in hand-off token (#handoff=…) is read by the sign-in form, not a page: the app underneath is Home.
  if (/^#?handoff=/.test(hash)) return 'home';
  const page = hash.replace(/^#/, '').split('?')[0] || 'home';
  const [base = '', ...rest] = page.split('/');
  if (rest.length && deepRedirects.has(base)) return [redirects[base], ...rest].join('/');
  return redirects[page] ?? page;
}

/** True when the shell has a real page for this resolved route (including deep links). */
export function isKnownPage(page: string): boolean {
  const base = page.split('/')[0] || 'home';
  return knownBases.has(base);
}

/** Browser tab title for a resolved route. */
export function pageTitle(page: string): string {
  const base = page.split('/')[0] || 'home';
  const labels: Record<string, string> = {
    home: 'Home',
    collections: 'Collections',
    questions: 'Questions for you',
    today: 'Today',
    approvals: 'Approvals',
    inbox: 'Inbox',
    customers: page.includes('/') ? 'Customer' : 'Customers',
    calls: 'Calls',
    reports: 'Reports',
    integrations: 'Integrations',
    policy: 'Playbooks',
    'demo-mailbox': 'Demo mailbox',
    'demo-slack': 'Demo Slack',
    cash: 'Cash',
    credit: 'Credit',
    settings: 'Settings',
    observability: 'Observability',
  };
  const label = labels[base];
  return label ? `${label} · Alder` : 'Alder · Accounts receivable';
}

/** Splits `customers/<id>` (or `approvals/<id>`) into the page and the record it opens. */
export function pageRoute(page: string): { page: string; id: string | null } {
  const [base = '', ...rest] = page.split('/');
  const id = rest.join('/');
  if (!id) return { page: base, id: null };
  try { return { page: base, id: decodeURIComponent(id) }; } catch { return { page: base, id }; }
}

/**
 * The older queues that Collections replaced (2026-10-31). Their hashes and deep links still open, as tabs under
 * Collections in the nav; the Inbox keeps every reply option.
 */
export const needsYouTabs = ['approvals', 'inbox', 'today'] as const;
export type NeedsYouTab = typeof needsYouTabs[number];
export const isNeedsYouTab = (page: string): page is NeedsYouTab => (needsYouTabs as readonly string[]).includes(page);

/** The navigation item a page belongs to, for the active highlight. Demo stand-ins and Observability live under Settings. */
export const navigationOwner = (page: string) => {
  if (isNeedsYouTab(page)) return 'collections';
  if (page === 'observability' || page === 'demo-mailbox' || page === 'demo-slack') return 'settings';
  return page;
};

/**
 * Primary sidebar, grouped like a working desk (2026-10-31). Work: the daily loop. Records: what the work is about.
 * Admin: how Amy works (Playbooks is the Policy page). Integrations is listed for owners, who are the only ones who can
 * change it. `demo` still unlocks demo routes; they are not listed here.
 */
export function navigationItems(roles: readonly string[], _demo: boolean, modules: Modules = {}): NavigationItem[] {
  if (modules.simple) return simpleNavigationItems(roles);
  const items: NavigationItem[] = [
    { id: 'home', label: 'Home', icon: 'home', group: 'work' },
    { id: 'collections', label: 'Collections', icon: 'collections', group: 'work' },
    { id: 'questions', label: 'Questions', icon: 'questions', group: 'work' },
    { id: 'calls', label: 'Calls', icon: 'calls', group: 'work' },
    { id: 'cash', label: 'Cash', icon: 'cash', group: 'work' },
    { id: 'customers', label: 'Customers', icon: 'customers', group: 'records' },
  ];
  if (modules.credit) items.push({ id: 'credit', label: 'Credit', icon: 'credit', group: 'records' });
  items.push({ id: 'reports', label: 'Reports', icon: 'reports', group: 'records' },
    { id: 'policy', label: 'Playbooks', icon: 'policy', group: 'admin' });
  if (roles.includes('owner')) items.push({ id: 'integrations', label: 'Integrations', icon: 'integrations', group: 'admin' });
  items.push({ id: 'settings', label: 'Settings', icon: 'settings', group: 'admin' });
  return items;
}

/** What a host turns on: the credit module, and the simple view (UI_MODE=simple) that shows reviewers only the review loop. */
export interface Modules { credit?: boolean; simple?: boolean }

/**
 * The simple view (UI_MODE=simple, AIprise 2026-11-02): four places for reviewers, named for what a reviewer does. Review holds
 * the emails to approve and Amy's questions; Customers; Report is Reports; Instructions is the policy in the client's words.
 * Owners also get Integrations and Settings. Home, Calls, Cash and Credit are not listed, and their links open Review.
 */
export function simpleNavigationItems(roles: readonly string[]): NavigationItem[] {
  const items: NavigationItem[] = [
    { id: 'collections', label: 'Review', icon: 'collections', group: 'work' },
    { id: 'customers', label: 'Customers', icon: 'customers', group: 'records' },
    { id: 'reports', label: 'Report', icon: 'reports', group: 'records' },
    { id: 'policy', label: 'Instructions', icon: 'policy', group: 'admin' },
  ];
  if (roles.includes('owner')) items.push({ id: 'integrations', label: 'Integrations', icon: 'integrations', group: 'admin' },
    { id: 'settings', label: 'Settings', icon: 'settings', group: 'admin' });
  return items;
}

const hiddenInSimple = new Set(['home', 'calls', 'cash', 'credit', 'questions', 'today', 'approvals']);
/** In the simple view the pages it does not list open Review, so a bookmark or the empty address never lands on a hidden page. */
export function simplePage(page: string): string {
  const base = page.split('/')[0] || 'home';
  return hiddenInSimple.has(base) ? 'collections' : page;
}

/** ⌘K palette rows: jump around the desk, then ask the agent. Kept free of React for unit tests. */
export interface PaletteCommand {
  id: string;
  label: string;
  keywords: string[];
  href?: string;
  action?: 'ask';
}

export function paletteCommands(
  roles: readonly string[],
  demo: boolean,
  modules: Modules = {},
  persona = 'Amy',
): PaletteCommand[] {
  if (modules.simple) {
    const keywords: Record<string, string[]> = { collections: ['drafts', 'approve', 'replies', 'questions'],
      customers: ['accounts'], reports: ['scorecard', 'aging'], policy: ['policy', 'rules', 'playbook', 'instructions'],
      integrations: ['gmail', 'sequence', 'slack'], settings: ['account', 'signature'] };
    return [...simpleNavigationItems(roles).map(i => ({ id: i.id, label: i.label, keywords: keywords[i.id] ?? [], href: i.id })),
      { id: 'ask', label: `Ask or tell ${persona}`, keywords: ['chat', 'assistant', 'ask'], action: 'ask' as const }];
  }
  const go: PaletteCommand[] = [
    { id: 'home', label: 'Home', keywords: ['dashboard', 'briefing'], href: 'home' },
    { id: 'collections', label: 'Collections', keywords: ['needs you', 'queue', 'approvals', 'drafts', 'cases'], href: 'collections' },
    { id: 'questions', label: 'Questions for you', keywords: ['ask', 'answer', 'knowledge', 'carol asks'], href: 'questions' },
    { id: 'customers', label: 'Customers', keywords: ['accounts'], href: 'customers' },
    { id: 'cash', label: 'Cash', keywords: ['calendar', 'forecast', 'promises'], href: 'cash' },
    { id: 'calls', label: 'Calls', keywords: ['phone', 'voice'], href: 'calls' },
    { id: 'reports', label: 'Reports', keywords: ['aging', 'dso'], href: 'reports' },
  ];
  if (modules.credit) go.push({ id: 'credit', label: 'Credit', keywords: ['limit', 'application'], href: 'credit' });
  go.push(
    { id: 'settings', label: 'Settings', keywords: ['account', 'appearance', 'signature'], href: 'settings' },
    { id: 'policy', label: 'Playbooks', keywords: ['policy', 'autonomy', 'playbook', 'rules'], href: 'policy' },
  );
  if (roles.includes('owner')) {
    go.push({ id: 'integrations', label: 'Integrations', keywords: ['gmail', 'slack', 'bank', 'netsuite'], href: 'integrations' });
  }
  if (demo) {
    go.push(
      { id: 'demo-mailbox', label: 'Demo mailbox', keywords: ['mail', 'inbox'], href: 'demo-mailbox' },
      { id: 'demo-slack', label: 'Demo Slack', keywords: ['slack'], href: 'demo-slack' },
    );
  }
  go.push({
    id: 'ask',
    label: `Ask or tell ${persona}`,
    keywords: ['amy', 'carol', 'chat', 'assistant', 'ask'],
    action: 'ask',
  });
  return go;
}
